import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { AmazonMark, GoogleMark, MicrosoftMark, NpmChip, WalmartMark } from "./brand-marks";
import { HypeStackCard } from "./hype-stack-card";
import { SocialIconLink } from "./social-icons";
import avatarSrc from "@/assets/images/avatar.png";
import { NewsletterSignup } from "@/features/newsletter/modules/newsletter-signup/newsletter-signup";
import { profile, socials } from "@/features/portfolio/config";

/**
 * The proof line as objects instead of prose: the npm downloads pill and a
 * logo wall of companies the libraries run in, in their brand colors.
 */
function StatsRow() {
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
      <span className="flex items-center gap-1.5 rounded-full border border-border bg-card py-1 pl-1.5 pr-3 text-xs font-semibold text-card-foreground shadow-sm">
        <NpmChip />
        <span>
          <strong className="font-bold">{profile.downloadsHighlight}</strong>
          {profile.downloadsRest}
        </span>
      </span>
      <span className="flex items-center gap-2.5">
        <span className="mr-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/50">
          used by
        </span>
        <MicrosoftMark className="size-[18px]" />
        <AmazonMark className="size-[18px] translate-y-[2px] text-foreground/80" />
        <GoogleMark className="size-[18px]" />
        <WalmartMark className="size-[18px]" />
      </span>
    </div>
  );
}

/**
 * The left rail: who I am, the newsletter, where else to find me, and the
 * Hype Stack plug. Sticky on desktop, first in flow on mobile.
 */
export function ProfilePanel() {
  return (
    // On viewports tall enough for the whole rail (min-height 960px), the
    // rail's height exactly complements the page's lg:py-16 (4rem top + 4rem
    // bottom), so the left column is precisely one viewport tall: the page
    // never gains scroll overflow from this column and nothing here ever
    // moves. On shorter screens the rail just flows with the page and the
    // Hype Stack card swaps to its pinned pill face.
    <aside className="flex flex-col gap-6 lg:[@media(min-height:960px)]:h-[calc(100svh-8rem)]">
      <div className="flex flex-col gap-6 lg:min-h-0 lg:flex-1">
      <div>
        <img
          src={avatarSrc}
          alt={profile.name}
          className="size-40 rounded-full border border-border bg-accent object-cover sm:size-52"
        />
        <h1 className="mt-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{profile.name}</h1>
        <p className="mt-4 text-lg text-foreground">{profile.bio}</p>
        <div className="mt-4">
          <StatsRow />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-lg text-muted-foreground">{profile.sharing}</p>
        <NewsletterSignup
          source="portfolio"
          layout="compact"
          title={profile.newsletterTitle}
          description={profile.newsletterDescription}
        />
        <Link
          to="/newsletter"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Read past issues in the browser
          <ArrowRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex items-center gap-4">
        {socials.map((link) => (
          <SocialIconLink key={link.id} link={link} />
        ))}
      </div>
      </div>

      <HypeStackCard />
    </aside>
  );
}
